<template>
    <page :busy="busy">
        <b-container fluid v-if="chatSettings">
            <b-row>
                <b-col lg="4" md="8" sm="12">
                    <b-row>
                        <b-col :md="12">
                            <br>
                            <table class="table">
                                <tbody>
                                    <tr>
                                        <td>Use company name ({{userCompany}}) in guest communications?</td>
                                        <td style="text-align: right;">
                                            <form-switch
                                                v-model="chatSettings.use_company_for_subscriber_name"
                                            />
                                        </td>
                                    </tr>                                    
                                    <tr>
                                        <td>Use subscriber name ({{ userSubscriberName }}) in guest communications?</td>
                                        <td style="text-align: right;">
                                            <form-switch
                                                v-model="chatSettings.use_subscriber_name_for_responses"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Shorten team member last name to first initial only?</td>
                                        <td style="text-align: right;">
                                            <form-switch
                                                v-model="chatSettings.anonymize_team_member_last_name"
                                                @change="updateSettings(chatSettings)"                                                
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Display trigger messages collapsed by default?</td>
                                        <td style="text-align: right;">
                                            <form-switch
                                                v-model="chatSettings.trigger_messages_collapsed"
                                                @change="updateSettings(chatSettings)"                                                
                                            />
                                        </td>
                                    </tr>                                    
                                    <tr>
                                        <td>Trigger creates last message received event?</td>
                                        <td style="text-align: right;">
                                            <form-switch
                                                v-model="chatSettings.trigger_creates_event"
                                                @change="updateSettings(chatSettings)"                                                
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Disable guest notifications for replies via VRBO?</td>
                                        <td style="text-align: right;">
                                            <form-switch
                                                v-model="chatSettings.disable_notifications_for_reply_via_vrbo"
                                                @change="updateSettings(chatSettings)"                                                
                                            />
                                        </td>
                                    </tr>
                                    <tr v-if="chatSettings.lodgix_ai_settings.ai_assistant_enabled">
                                        <td>AI Assistant for Inquiries? (Beta)</td>
                                        <td style="text-align: right;">
                                            <form-switch
                                                v-model="chatSettings.lodgix_ai_settings.inquiry_ai_assistant"
                                                @change="updateSettings(chatSettings)"                                                
                                            />
                                        </td>
                                    </tr>
                                    <tr v-if="chatSettings.lodgix_ai_settings.ai_assistant_enabled">
                                        <td>AI Assistant for Reservations? (Beta)</td>
                                        <td style="text-align: right;">
                                            <form-switch
                                                v-model="chatSettings.lodgix_ai_settings.reservation_ai_assistant"
                                                @change="updateSettings(chatSettings)"                                                
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td colspan="2">
                                            <form-select
                                                :options="teamMembersOptions"
                                                floatingLabel="Reservation Default Assignee"
                                                v-model="chatSettings.default_assignee"
                                                @change="updateSettings(chatSettings)"
                                            />
                                            <br>
                                            <form-select
                                                :options="teamMembersOptions"
                                                floatingLabel="Inquiry Default Assignee"
                                                v-model="chatSettings.inquiry_default_assignee"
                                                @change="updateSettings(chatSettings)"
                                            />
                                        </td>
                                    </tr>                                    
                                </tbody>
                            </table>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
        </b-container>
    </page>
</template>

<script>
    import api from '../../../api';
    import { mapGetters } from 'vuex';
    import { formatResponseError } from '../../../utils/api';
    import {
        CHAT_SETTINGS_SETTINGS_ENDPOINT,
        TRIGGER_ENDPOINT,
    } from '../../../endpoints';
    import FormSwitch from '../../generic/form-switch/form-switch.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import Page from '../../generic/page/page.vue';
    export default {
        components: {
            FormSelect,
            FormSwitch,
            Page,
        },
        data() {
            return {
                busy: false,
                chatSettings: null,
                isInitialLoad: true,
                updateTimeout: null,
                teamMembersOptions: [],
            };
        },
        mounted() {
            this.fetchAllData();
        },
        computed: {
            ...mapGetters('header', ['userSubscriberId', 'userCompany', 'userSubscriberName']),
        },
        methods: {
            async updateSettings(chatSettings) {
                try {
                    this.busy = true;
                    await api.patch(CHAT_SETTINGS_SETTINGS_ENDPOINT(), chatSettings);
                    this.successToast({ text: 'Settings successfully saved' });
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not save settings' });
                } finally {
                    this.busy = false;
                }
            },
            async debounceUpdateSettings() {
                if (this.updateTimeout) clearTimeout(this.updateTimeout);
                this.updateTimeout = setTimeout(() => {
                    this.updateSettings(this.chatSettings);
                }, 500);
            },            
            async fetchAllData() {
                this.busy = true;
                await this.fetchSettings();
                await Promise.all([this.fetchTeamMembers(),]);
                this.busy = false;
            },
            async fetchSettings() {
                try {
                    this.busy = true;
                    const { data } = await api.get(CHAT_SETTINGS_SETTINGS_ENDPOINT());
                    this.chatSettings = data;
                } catch (e) {
                    const { generic } = formatResponseError(e);
                    this.errorToast({ text: generic || 'Could not fetch settings' });
                } finally {
                    this.busy = false;
                    this.$nextTick(() => {
                        this.isInitialLoad = false;
                    });                    
                }
            },
            async fetchTeamMembers() {
                try {
                    const { data } = await api.get(`${TRIGGER_ENDPOINT()}team-members/`);
                    const teamMembersOptions = data.map((item) => {
                        return {
                            value: item.id,
                            text: `${item.first_name} ${item.last_name}`,
                        };
                    });
    
                    this.teamMembersOptions = [
                        { value: null, text: 'Not assigned' },
                        { value: this.userSubscriberId, text: 'Subscriber' },
                        ...teamMembersOptions,
                    ];
                    if (
                        !this.teamMembersOptions.find(
                            (item) => item.value === this.chatSettings.default_assignee
                        )
                    ) {
                        this.chatSettings.default_assignee = null;
                    }
                } catch (e) {
                    const { generic } = formatResponseError(e);
    
                    this.errorToast({ text: generic || 'Could not fetch team members' });
                }
            },            
        },
        watch: {
            'chatSettings.use_company_for_subscriber_name'(newVal, oldVal) {
                if (this.isInitialLoad) return;
        
                if (newVal !== oldVal) {
                    this.chatSettings.use_subscriber_name_for_responses = !newVal;
                    this.updateSettings(this.chatSettings);
                }
            },
            'chatSettings.use_subscriber_name_for_responses'(newVal, oldVal) {
                if (this.isInitialLoad) return;
        
                if (newVal !== oldVal) {
                    this.chatSettings.use_company_for_subscriber_name = !newVal;
                    this.debounceUpdateSettings(this.chatSettings);
                }
            }
        },    
    };
</script>

<style scoped lang="scss">
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';
    :deep(*) {
        font-family: 'Lato', sans-serif !important;
    }
    
    :deep(.form-group) {
        margin: 0 !important;
    }
</style>
